import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { MatTable, MatTableModule } from '@angular/material/table';
import { CdkDragDrop, CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { DistancePipe, IconType } from '@kinomap-dev/ng-ui-lib/core';

import { CountryCell } from './cells/country/country-cell';
import { ColumnHeader } from './column-header/column-header';
import { ColorService } from '../../services/color.service';
import { DatePipe, DecimalPipe, NgClass, UpperCasePipe } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { LabelsCell } from './cells/labels-cell/labels-cell';
import { SettingsService } from '../../services/settings.service';
import { computeScrollPercent } from '../../utils/compute-scroll-percent';
import { IconComponent, SkeletonComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { RouterLink } from '@angular/router';
import { Chip } from '../chip/chip';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { ColumnVisibilityMenu } from './column-visibility-menu/column-visibility-menu';

@Component({
  selector: 'app-table',
  imports: [
    MatTableModule,
    CdkDropList,
    CdkDrag,
    NgClass,
    IconComponent,
    ColumnHeader,
    CountryCell,
    ScrollingModule,
    LabelsCell,
    SkeletonComponent,
    DistancePipe,
    DecimalPipe,
    DatePipe,
    UpperCasePipe,
    RouterLink,
    Chip,
    CdkMenuTrigger,
    TranslateModule,
    ColumnVisibilityMenu,
  ],
  templateUrl: './table.html',
  styleUrl: './table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '(window:resize)': 'onResize()',
    '[style.--shadow-opacity]': 'scrollPercent()',
  },
})
export class Table {
  readonly colorService: ColorService = inject(ColorService);
  readonly settingService: SettingsService = inject(SettingsService);

  readonly actions: InputSignal<IconType[]> = input.required<IconType[]>();
  readonly columns: ModelSignal<string[]> = model.required<string[]>();
  readonly datas: InputSignal<any[] | undefined> = input<any[]>();

  readonly skeletonCount: InputSignal<number> = input(10);
  readonly skeletonRows: Signal<{}[]> = computed(() => Array(this.skeletonCount()).fill({}));
  readonly tableScrollContainerRef: Signal<ElementRef<HTMLDivElement>> =
    viewChild.required<ElementRef<HTMLDivElement>>('table');
  readonly tableRef: Signal<MatTable<unknown>> = viewChild.required(MatTable);
  readonly isLoading: Signal<boolean> = computed(() => !this.datas());
  readonly scrollPercent: WritableSignal<number> = signal(0);
  readonly columnsMenuOpen: WritableSignal<boolean> = signal(false);
  readonly columnsMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  elementChecked: OutputEmitterRef<number> = output<number>();

  drop(event: CdkDragDrop<string[]>): void {
    const columns: string[] = [...this.columns()];
    if (event.previousIndex === columns.length - 1 || event.currentIndex === 0) return;
    moveItemInArray(columns, event.previousIndex, event.currentIndex);
    this.columns.set(columns);
  }

  onResize(): void {
    this.scrollPercent.set(
      computeScrollPercent(
        this.tableScrollContainerRef().nativeElement.scrollWidth,
        this.tableScrollContainerRef().nativeElement.clientWidth,
        0.03,
        0.2,
      ),
    );
  }

  constructor() {
    effect(() => {
      this.datas();
      this.columns();
      this.onResize();
    });
  }

  protected refreshSticky(): void {
    this.tableRef().updateStickyHeaderRowStyles();
  }
}
